import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { YoutubeApiClient } from '../../services/YouTube';
import {
  Box,
  Button,
  ScrollAreaRoot,
  ScrollAreaScrollbar,
  ScrollAreaViewport,
  Text,
  styled,
} from '@streamelements/frontend-ui';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { ReactNode, useState } from 'react';
import { Destination, DestinationsStorageApiClient } from '../../services/DestinationsApiClient';
import { z } from 'zod';
import { providerIcons } from '../../components/ProviderIcon';
import { AspectRatio } from '../../components/MultiStreamingTargetList/AspectRatio';
import { promisefy } from '../../utils/promisefy';
import { PrimaryOutputIcon } from '../../components/Icons';

const RadioGroupRoot = styled(RadioGroup.Root, {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  px: '1rem',
  flex: 1,
});

const RadioItem = styled(RadioGroup.Item, {
  width: '100%',
  py: '0.5rem',
  px: '1rem',
  bgc: '$uiSecondary',
  border: '1px solid $divider',
  boxShadow: '$1',
  br: '$base',
  textAlign: 'left',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '1rem',
  outline: 'none',
  cursor: 'pointer',

  label: { display: 'flex', flexDirection: 'column', gap: '0.125rem', cursor: 'inherit' },

  '@supports (text-align: start)': {
    textAlign: 'start',
  },

  '&:focus-visible': {
    outline: '2px solid $colors$uiPrimary50',
    outlineOffset: '2px',
  },

  '&:focus-visible, &:hover': {
    bgc: '$uiQuaternary',
  },
});

const RadioIndicatorRoot = styled('span', {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  justifyContent: 'center',
  width: '1.25rem',
  height: '1.25rem',
  borderRadius: '50%',
  border: '1px solid $divider',

  '[data-state="checked"] &': {
    borderColor: '$colors$uiPrimary50',
  },
});

const RadioIndicator = styled(RadioGroup.Indicator, {
  width: '0.75rem',
  height: '0.75rem',
  borderRadius: '50%',
  backgroundColor: '$colors$uiPrimaryMain',
});

const searchValidation = z.object({
  destination_id: z.string({
    required_error: 'Destination ID is required',
  }),
  output_id: z.string({
    required_error: 'Output ID is required',
  }),
  streamTitle: z.string(),
});

export const Route = createFileRoute('/select-output/youtube')({
  component: YouTubeBroadcastsList,
  validateSearch: searchValidation,
  loaderDeps: ({ search }) => {
    return search;
  },
  loader: async ({
    deps: { destination_id: destinationId, output_id: outputId, streamTitle },
    context: { authContextProvider, serviceEnvironment },
  }) => {
    const destinationsStorageApiClient = new DestinationsStorageApiClient({
      authContextProvider,
      serviceEnvironment,
    });

    const destinationMetadata = (await destinationsStorageApiClient.getDestinationById({
      id: destinationId,
    })) as Destination<'youtube'>;

    if (!destinationMetadata) {
      throw new Error('Destination not found');
    }

    const broadcasts = await new YoutubeApiClient({
      serviceEnvironment,
      authContextProvider,
      destinationMetadata,
    }).fetchBroadcasts({ status: 'upcoming' });

    const { output, siblingOutputs } = destinationMetadata.outputs.reduce(
      (acc, curr) => {
        if (curr.id === outputId) {
          acc.output = curr;
        } else {
          acc.siblingOutputs.push(curr);
        }

        return acc;
      },
      {
        siblingOutputs: [] as Destination<'youtube'>['outputs'][number][],
        output: null as Destination<'youtube'>['outputs'][number] | null,
      }
    );

    const { [output?.videoCompositionId ?? 'default']: videoComposition } = await promisefy(
      window.host.getAllVideoCompositions
    )();

    const availableBroadcasts = broadcasts.filter((broadcast) => {
      return !siblingOutputs.some((output) => output.auxiliaryData?.broadcastId === broadcast.id);
    });

    return {
      videoComposition,
      destinationMetadata,
      output,
      broadcasts: availableBroadcasts.slice(0, 10),
      streamTitle: decodeURIComponent(streamTitle),
    };
  },
  pendingComponent: PendingComponent,
  errorComponent: ErrorComponent,
});

function PendingComponent() {
  return (
    <Box css={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '1.25rem' }}>
      {Array(3)
        .fill(0)
        .map((_, index) => (
          <Box
            css={{
              width: '100%',
              height: 54,
              backgroundColor: '$uiSecondary',
              border: '1px solid $divider',
              br: '$base',
            }}
            key={index}
          />
        ))}
    </Box>
  );
}

function ErrorComponent() {
  return <div>Failed to fetch broadcasts. Close the window and try again.</div>;
}

function BroadcastItem({
  id,
  title,
  description,
  scheduledStartTime,
}: {
  id: string;
  title: ReactNode;
  description?: string;
  scheduledStartTime?: string;
}) {
  return (
    <RadioItem value={id}>
      <RadioIndicatorRoot>
        <RadioIndicator />
      </RadioIndicatorRoot>

      <label htmlFor={id}>
        <Text.Body>{title}</Text.Body>
        <Text.Body variant="caption" css={{ color: '$textPrimary50' }}>
          {[description, scheduledStartTime ? new Date(scheduledStartTime).toLocaleString() : '']
            .filter(Boolean)
            .join(' | ')}
        </Text.Body>
      </label>
    </RadioItem>
  );
}

function YouTubeBroadcastsList() {
  const { broadcasts, output, videoComposition, streamTitle } = Route.useLoaderData();
  const [value, setValue] = useState<string | null>(null);

  const submitMutation = useMutation({
    mutationFn: async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const values = new FormData(event.target as HTMLFormElement);
      const broadcastId = values.get('broadcastId') as string;

      if (!broadcastId) {
        throw new Error('A broadcast must be selected');
      }

      window.host.endModalDialog(broadcastId, () => {});

      return broadcastId;
    },
  });

  const Icon = providerIcons['youtube'];

  return (
    <Box
      as="form"
      css={{ display: 'flex', flexDirection: 'column', flex: 1, pt: '1rem', overflow: 'auto' }}
      onSubmit={submitMutation.mutate}
    >
      <Box css={{ mx: '1rem', mb: '1rem', borderBottom: '1px solid $divider', pb: '1rem' }}>
        <Text.Subtitle
          css={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}
          weight="bold"
        >
          <Icon css={{ verticalAlign: 'middle' }} /> {output?.name} <AspectRatio {...videoComposition.videoFrame} />
          {output?.auxiliaryData.isPrimaryOutput && <PrimaryOutputIcon />}
        </Text.Subtitle>
        <Text.Body css={{ color: '$textPrimary50' }}>
          Select a scheduled or create a new broadcast to start streaming on YouTube
        </Text.Body>
      </Box>

      <ScrollAreaRoot
        css={{ overflow: 'auto', height: 'auto', display: 'flex', flexDirection: 'column', flex: 1 }}
        type="always"
      >
        <ScrollAreaViewport>
          <RadioGroupRoot
            defaultValue={broadcasts[0]?.id}
            name="broadcastId"
            css={{ mb: '1rem' }}
            onValueChange={setValue}
            value={value ?? broadcasts[0]?.id}
          >
            <Text.Body css={{ color: '$textPrimary70' }}>Create a new broadcast </Text.Body>
            <BroadcastItem
              id="new"
              title={<Text.Body>{streamTitle}</Text.Body>}
              description="Create a new broadcast and start streaming immediately. "
            />

            <Text.Body css={{ mt: '1rem', color: '$textPrimary70' }}>Choose a scheduled broadcast</Text.Body>

            {broadcasts.map((broadcast) => {
              const isValidDate = !!broadcast.snippet.scheduledStartTime;

              if (!isValidDate) {
                return null;
              }

              return (
                <BroadcastItem
                  key={broadcast.id}
                  id={broadcast.id}
                  title={broadcast.snippet.title}
                  description={broadcast.snippet.description}
                  scheduledStartTime={broadcast.snippet.scheduledStartTime}
                />
              );
            })}
          </RadioGroupRoot>
        </ScrollAreaViewport>
        <ScrollAreaScrollbar />
      </ScrollAreaRoot>

      <Box
        css={{
          py: '0.5rem',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Button
          type="submit"
          density="compact"
          css={{ mx: 'auto', '@media (max-width: 420px)': { width: 'calc(100% - 2rem)' } }}
          disabled={!value}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
}
